/**
 * Authentication Manager
 * Handles all authentication-related functionality for Doc2Plot
 */

import { checkAuth } from '../lib/auth-utils'
import { showAuthModal } from '../components/auth-modal'
import { updateProfileConnectionStatus } from '../components/user-profile'
import { logger } from '../utils/logger'

export class AuthManager {
  private isAuthenticated: boolean = false

  constructor() {
    // Initialize authentication state
  }

  public getIsAuthenticated(): boolean {
    return this.isAuthenticated
  }

  public setIsAuthenticated(value: boolean): void {
    this.isAuthenticated = value
  }

  /**
   * Check authentication status and handle redirects
   */
  public async checkAuthentication(appContainer: HTMLElement): Promise<void> {
    try {
      logger.auth('Starting authentication check with Better Auth...')
      
      // Check if this is a redirect from email verification
      const urlParams = new URLSearchParams(window.location.search)
      const isFromVerification = urlParams.has('verified')
      
      // Handle email verification redirect
      if (isFromVerification) {
        logger.auth('Detected email verification redirect')
        
        // Clear URL parameters
        const newUrl = window.location.pathname
        window.history.replaceState({}, document.title, newUrl)
        
        // Show success message
        const toast = document.createElement('div')
        toast.className = 'toast toast-success'
        toast.textContent = 'Email verified successfully! You can now sign in.'
        document.body.appendChild(toast)
        
        // Remove toast after 5 seconds
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast)
          }
        }, 5000)
      }
      
      // Check authentication status
      const authResult = await checkAuth()
      
      if (authResult.authenticated && authResult.user) {
        logger.auth('User is authenticated:', authResult.user.email)
        this.isAuthenticated = true
        
        // Update profile connection status
        updateProfileConnectionStatus(true)
        
        // Show main app
        this.showMainApp(appContainer)
      } else {
        logger.auth('User is not authenticated, showing login screen')
        this.isAuthenticated = false
        
        // Show login screen
        this.showLoginScreen(appContainer)
      }
    } catch (error) {
      logger.appError('Authentication check failed:', error)
      this.isAuthenticated = false
      this.showLoginScreen(appContainer)
    }
  }

  /**
   * Show login screen with options
   */
  private showLoginScreen(container: HTMLElement): void {
    container.innerHTML = `
      <div class="min-h-screen neobrutal flex flex-col justify-center py-12 px-4">
        <div class="max-w-md w-full mx-auto">
          <div class="card">
            <div class="card-header">
              <h2 class="text-xl font-semibold">Welcome to Doc2Plot</h2>
              <p class="text-sm text-gray-600">Sign in or create an account</p>
            </div>
            <div class="card-body">
              <div class="space-y-3">
                <button id="login-btn" class="btn btn-primary w-full" style="border: 3px solid #111827; border-radius: 8px; background-color: #86efac; color: #111827; box-shadow: 4px 4px 0 #111827;">Sign In</button>
                <button id="register-btn" class="btn btn-outline w-full" style="border: 3px solid #111827; border-radius: 8px; background-color: #ffffff; color: #111827; box-shadow: 4px 4px 0 #111827;">Create Account</button>
              </div>
              <div class="text-center mt-4">
                <span class="text-xs text-gray-600">Alpha 0.5.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // Add event listeners
    const loginBtn = container.querySelector('#login-btn')
    const registerBtn = container.querySelector('#register-btn')
    
    loginBtn?.addEventListener('click', () => {
      showAuthModal(true, (user) => {
        logger.auth('User logged in:', user)
        this.isAuthenticated = true
        this.showMainApp(container)
      })
    })

    registerBtn?.addEventListener('click', () => {
      showAuthModal(false, (user) => {
        logger.auth('User registered:', user)
        this.isAuthenticated = true
        this.showMainApp(container)
      })
    })
  }

  // Removed showLoginPage and handleLogin - now using showAuthModal

  // Removed showRegistrationPage and handleRegistration - now using showAuthModal

  // Removed showEmailVerificationPage and handleResendVerification - now handled by auth modal

  // Removed showForgotPasswordModal - now handled by auth modal

  /**
   * Show main app (placeholder - will be handled by main App class)
   */
  private showMainApp(container: HTMLElement): void {
    // Clear the container and trigger main app initialization
    container.innerHTML = ''
    
    // Dispatch a custom event to notify the main App class
    const event = new CustomEvent('auth-success', {
      detail: { container }
    })
    window.dispatchEvent(event)
  }
}
